/**
 * WB4.4 — Per-domain detail cockpit invariants (inspector signals · auxiliary tabs · action surfaces).
 * Complements role cockpit (`workbench-role-cockpit-config`) — domain layer only, no new shells.
 */

import type { WorkbenchActionSurface } from './action-types';
import type { InspectorSignal } from './workbench-role-cockpit-config';
import { REFERENCE_DOMAINS } from './domain-action-rule-table';
import { resolveWorkbenchGrammarProxy } from './workbench-domain-grammar-proxy';
import {
  formatInspectorSignalRawValue,
  isInspectorSignalFieldPresent,
  readInspectorSignalObjectField,
  resolveInspectorSignalDisplayValue,
  resolveInspectorSignalSpecForId,
} from './workbench-inspector-signal-field-contract';

export type DomainCockpitAuxiliaryTabKey =
  | 'timeline'
  | 'audit'
  | 'evidence'
  | 'validation'
  | 'explain';

export type DomainCockpitPilotKey =
  | 'finance'
  | 'entity'
  | 'entity_customer'
  | 'entity_supplier'
  | 'finance_collection'
  | 'knowledge_document'
  | 'item_sellable'
  | 'service_item'
  | 'manufacturing_bom'
  | 'manufacturing_production_order'
  | 'warehouse_picking'
  | 'order'
  | 'inventory_shortage'
  | 'task_execution'
  | 'reference';

export interface DomainCockpitInvariant {
  pilotKey: DomainCockpitPilotKey;
  inspectorSignalPriority: readonly string[];
  auxiliaryTabPriority: readonly DomainCockpitAuxiliaryTabKey[];
  actionSurfaceDefaults: readonly WorkbenchActionSurface[];
  inspectorDefaultTab: 'flows' | 'advanced';
  aiLayersInInspector: boolean;
}

const FINANCE_DOMAINS = ['finance_state', 'finance_flow', 'finance_budget'] as const;
const ENTITY_DOMAINS = ['internal_entity', 'external_entity'] as const;
const ENTITY_CUSTOMER_PROXY_DOMAINS = ['customer', 'counterparty'] as const;
const ENTITY_SUPPLIER_PROXY_DOMAINS = ['supplier'] as const;
const FINANCE_COLLECTION_PROXY_DOMAINS = ['collection', 'ar_collection'] as const;
const KNOWLEDGE_DOCUMENT_PROXY_DOMAINS = [
  'document',
  'document_report',
  'document_policy',
  'document_plan',
  'org_document',
] as const;
const ITEM_SELLABLE_DOMAINS = ['sellable_item'] as const;
const SERVICE_ITEM_DOMAINS = ['service_item'] as const;
const MANUFACTURING_BOM_DOMAINS = ['manufacturing_bom'] as const;
const MANUFACTURING_PRODUCTION_ORDER_DOMAINS = ['production_order'] as const;
const WAREHOUSE_PICKING_DOMAINS = ['warehouse_picking_list'] as const;
const INVENTORY_SHORTAGE_DOMAINS = ['inventory_shortage'] as const;
const TASK_EXECUTION_DOMAINS = ['task_execution', 'assignment_execution'] as const;
const ORDER_PROXY_DOMAINS = ['order', 'sales_order'] as const;

export const DOMAIN_COCKPIT_PILOT_DOMAINS: readonly string[] = [
  ...FINANCE_DOMAINS,
  ...ENTITY_DOMAINS,
  ...ENTITY_CUSTOMER_PROXY_DOMAINS,
  ...ENTITY_SUPPLIER_PROXY_DOMAINS,
  ...FINANCE_COLLECTION_PROXY_DOMAINS,
  ...KNOWLEDGE_DOCUMENT_PROXY_DOMAINS,
  ...ITEM_SELLABLE_DOMAINS,
  ...SERVICE_ITEM_DOMAINS,
  ...MANUFACTURING_BOM_DOMAINS,
  ...MANUFACTURING_PRODUCTION_ORDER_DOMAINS,
  ...WAREHOUSE_PICKING_DOMAINS,
  ...INVENTORY_SHORTAGE_DOMAINS,
  ...TASK_EXECUTION_DOMAINS,
  ...ORDER_PROXY_DOMAINS,
  ...REFERENCE_DOMAINS,
];

export const DOMAIN_COCKPIT_INVARIANT_MATRIX: Readonly<
  Record<DomainCockpitPilotKey, DomainCockpitInvariant>
> = {
  finance: {
    pilotKey: 'finance',
    inspectorSignalPriority: [
      'overdue_amount',
      'voucher_status',
      'approval_flags',
      'credit_limit',
      'payment_behavior',
    ],
    auxiliaryTabPriority: ['audit', 'timeline', 'evidence', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'advanced',
    aiLayersInInspector: false,
  },
  entity: {
    pilotKey: 'entity',
    inspectorSignalPriority: [
      'relation_count',
      'permission_status',
      'linked_documents',
      'last_activity',
    ],
    auxiliaryTabPriority: ['timeline', 'evidence', 'audit', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: true,
  },
  entity_customer: {
    pilotKey: 'entity_customer',
    inspectorSignalPriority: ['ar_aging', 'credit_hold', 'relation_count', 'linked_documents'],
    auxiliaryTabPriority: ['audit', 'timeline', 'evidence', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'advanced',
    aiLayersInInspector: true,
  },
  entity_supplier: {
    pilotKey: 'entity_supplier',
    inspectorSignalPriority: [
      'relation_count',
      'permission_status',
      'linked_documents',
      'supplier_rating',
    ],
    auxiliaryTabPriority: ['evidence', 'timeline', 'audit', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: true,
  },
  finance_collection: {
    pilotKey: 'finance_collection',
    inspectorSignalPriority: ['ar_aging', 'credit_hold', 'overdue_amount', 'payment_behavior'],
    auxiliaryTabPriority: ['audit', 'timeline', 'evidence', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'advanced',
    aiLayersInInspector: false,
  },
  knowledge_document: {
    pilotKey: 'knowledge_document',
    inspectorSignalPriority: [
      'document_type',
      'linked_documents',
      'last_activity',
      'validation_status',
    ],
    auxiliaryTabPriority: ['evidence', 'audit', 'timeline', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: false,
  },
  item_sellable: {
    pilotKey: 'item_sellable',
    inspectorSignalPriority: [
      'stock_link',
      'reserved_quantity',
      'picking_availability',
      'catalog_status',
    ],
    auxiliaryTabPriority: ['evidence', 'timeline', 'audit', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: true,
  },
  service_item: {
    pilotKey: 'service_item',
    inspectorSignalPriority: [
      'service_status',
      'delivery_window',
      'linked_contract',
      'catalog_status',
    ],
    auxiliaryTabPriority: ['timeline', 'evidence', 'audit', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: true,
  },
  manufacturing_bom: {
    pilotKey: 'manufacturing_bom',
    inspectorSignalPriority: [
      'bom_version',
      'component_count',
      'revision_status',
      'approval_flags',
    ],
    auxiliaryTabPriority: ['timeline', 'validation', 'evidence', 'audit', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: false,
  },
  manufacturing_production_order: {
    pilotKey: 'manufacturing_production_order',
    inspectorSignalPriority: ['production_status', 'bom_link', 'quality_hold', 'schedule_variance'],
    auxiliaryTabPriority: ['timeline', 'validation', 'evidence', 'audit', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: false,
  },
  warehouse_picking: {
    pilotKey: 'warehouse_picking',
    inspectorSignalPriority: [
      'picking_status',
      'dispatch_queue',
      'stock_link',
      'picking_availability',
    ],
    auxiliaryTabPriority: ['evidence', 'timeline', 'audit', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command', 'board'],
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: false,
  },
  order: {
    pilotKey: 'order',
    inspectorSignalPriority: [
      'order_status',
      'fulfillment_stage',
      'linked_customer',
      'schedule_variance',
      'overdue_delivery',
      'delivery_evidence_missing',
      'production_queue',
    ],
    auxiliaryTabPriority: ['timeline', 'audit', 'evidence', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: true,
  },
  inventory_shortage: {
    pilotKey: 'inventory_shortage',
    inspectorSignalPriority: [
      'shortage_quantity',
      'replenishment_status',
      'stock_link',
      'picking_availability',
    ],
    auxiliaryTabPriority: ['evidence', 'timeline', 'audit', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'advanced',
    aiLayersInInspector: false,
  },
  task_execution: {
    pilotKey: 'task_execution',
    inspectorSignalPriority: [
      'task_status',
      'missing_evidence_tasks',
      'overdue_assignments',
      'assignee_link',
      'due_date',
      'workflow_stage',
    ],
    auxiliaryTabPriority: ['timeline', 'audit', 'evidence', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command', 'board'],
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: false,
  },
  reference: {
    pilotKey: 'reference',
    inspectorSignalPriority: [
      'reference_type',
      'linked_records',
      'last_activity',
      'validation_status',
    ],
    auxiliaryTabPriority: ['timeline', 'evidence', 'audit', 'validation', 'explain'],
    actionSurfaceDefaults: ['detail-header', 'inspector', 'command'],
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: false,
  },
};

const DOMAIN_TO_PILOT_KEY: Readonly<Record<string, DomainCockpitPilotKey>> = {
  finance_state: 'finance',
  finance_flow: 'finance',
  finance_budget: 'finance',
  internal_entity: 'entity',
  external_entity: 'entity',
  sellable_item: 'item_sellable',
  service_item: 'service_item',
  manufacturing_bom: 'manufacturing_bom',
  production_order: 'manufacturing_production_order',
  warehouse_picking_list: 'warehouse_picking',
  inventory_shortage: 'inventory_shortage',
  task_execution: 'task_execution',
  assignment_execution: 'task_execution',
  ...buildReferenceDomainPilotMap(),
};

function buildReferenceDomainPilotMap(): Record<string, DomainCockpitPilotKey> {
  const excluded = new Set<string>([
    ...KNOWLEDGE_DOCUMENT_PROXY_DOMAINS,
    'knowledge_report',
    'knowledge_policy',
    'knowledge_plan',
  ]);
  return REFERENCE_DOMAINS.reduce<Record<string, DomainCockpitPilotKey>>((acc, domain) => {
    if (!excluded.has(domain)) {
      acc[domain] = 'reference';
    }
    return acc;
  }, {});
}

/** Grammar-proxy business domains with dedicated cockpit depth (not canonical fallthrough). */
const PROXY_DOMAIN_TO_PILOT_KEY: Readonly<Record<string, DomainCockpitPilotKey>> = {
  customer: 'entity_customer',
  counterparty: 'entity_customer',
  supplier: 'entity_supplier',
  collection: 'finance_collection',
  ar_collection: 'finance_collection',
  order: 'order',
  sales_order: 'order',
  document: 'knowledge_document',
  document_report: 'knowledge_document',
  document_policy: 'knowledge_document',
  document_plan: 'knowledge_document',
  org_document: 'knowledge_document',
  knowledge_report: 'knowledge_document',
  knowledge_policy: 'knowledge_document',
  knowledge_plan: 'knowledge_document',
};

function formatSignalForPilot(
  pilotKey: DomainCockpitPilotKey,
  signalId: string,
  object: Record<string, unknown>
): string | undefined {
  const spec = resolveInspectorSignalSpecForId(pilotKey, signalId);
  if (!spec) {
    return formatInspectorSignalRawValue(readInspectorSignalObjectField(object, signalId));
  }
  return resolveInspectorSignalDisplayValue({
    object,
    objectFieldKeys: spec.objectFieldKeys,
    emptyDisplayDefault: spec.emptyDisplayDefault,
  });
}

function readObjectField(object: Record<string, unknown>, key: string): unknown;
function readObjectField(object: Record<string, unknown>, keys: readonly string[]): unknown;
function readObjectField(
  object: Record<string, unknown>,
  keyOrKeys: string | readonly string[]
): unknown {
  if (Array.isArray(keyOrKeys)) {
    for (const key of keyOrKeys) {
      const value = readInspectorSignalObjectField(object, key);
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return undefined;
  }
  return readInspectorSignalObjectField(object, keyOrKeys);
}

function isPilotSignalFieldPresent(
  pilotKey: DomainCockpitPilotKey,
  signalId: string,
  object: Record<string, unknown>
): boolean {
  const spec = resolveInspectorSignalSpecForId(pilotKey, signalId);
  if (!spec) {
    return false;
  }
  return isInspectorSignalFieldPresent(object, spec.objectFieldKeys);
}

function sortSignalsByDomainPriority(
  priority: readonly string[],
  signals: InspectorSignal[]
): InspectorSignal[] {
  const rank = new Map(priority.map((id, index) => [id, index]));
  return [...signals].sort((left, right) => {
    const leftRank = rank.get(left.id) ?? priority.length;
    const rightRank = rank.get(right.id) ?? priority.length;
    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }
    return left.label.localeCompare(right.label, 'ko');
  });
}

function buildFinanceDomainSignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'finance' as const;
  return [
    {
      id: 'overdue_amount',
      label: '연체 금액',
      value: formatSignalForPilot(pilotKey, 'overdue_amount', object),
      severity: 'danger',
      visible: Number(readObjectField(object, 'overdueAmount') ?? 0) > 0,
    },
    {
      id: 'voucher_status',
      label: '전표 상태',
      value: formatSignalForPilot(pilotKey, 'voucher_status', object),
      severity: 'info',
      visible: readObjectField(object, 'voucherStatus') !== undefined,
    },
    {
      id: 'approval_flags',
      label: '승인 플래그',
      value: formatSignalForPilot(pilotKey, 'approval_flags', object),
      severity: 'warning',
      visible: readObjectField(object, 'approvalFlags') !== undefined,
    },
    {
      id: 'credit_limit',
      label: '여신 한도',
      value: formatSignalForPilot(pilotKey, 'credit_limit', object),
      severity: 'info',
      visible: readObjectField(object, 'creditLimit') !== undefined,
    },
    {
      id: 'payment_behavior',
      label: '결제 성향',
      value: formatSignalForPilot(pilotKey, 'payment_behavior', object),
      severity: 'warning',
      visible: readObjectField(object, 'paymentBehavior') !== undefined,
    },
  ];
}

function buildEntityDomainSignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'entity' as const;
  return [
    {
      id: 'relation_count',
      label: '연결 관계',
      value: formatSignalForPilot(pilotKey, 'relation_count', object),
      severity: 'info',
      visible: readObjectField(object, 'relationCount') !== undefined,
    },
    {
      id: 'permission_status',
      label: '권한 상태',
      value: formatSignalForPilot(pilotKey, 'permission_status', object),
      severity: 'warning',
      visible: readObjectField(object, 'permissionStatus') !== undefined,
    },
    {
      id: 'linked_documents',
      label: '연결 문서',
      value: formatSignalForPilot(pilotKey, 'linked_documents', object),
      severity: 'info',
      visible: readObjectField(object, 'linkedDocuments') !== undefined,
    },
    {
      id: 'last_activity',
      label: '최근 활동',
      value: formatSignalForPilot(pilotKey, 'last_activity', object),
      severity: 'info',
      visible: readObjectField(object, 'lastActivityAt') !== undefined,
    },
  ];
}

function buildItemSellableDomainSignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'item_sellable' as const;
  return [
    {
      id: 'stock_link',
      label: '재고 연결',
      value: formatSignalForPilot(pilotKey, 'stock_link', object),
      severity: 'info',
      visible: readObjectField(object, 'stockLink') !== undefined,
    },
    {
      id: 'reserved_quantity',
      label: '예약 수량',
      value: formatSignalForPilot(pilotKey, 'reserved_quantity', object),
      severity: 'warning',
      visible: readObjectField(object, 'reservedQuantity') !== undefined,
    },
    {
      id: 'picking_availability',
      label: '피킹 가능',
      value: formatSignalForPilot(pilotKey, 'picking_availability', object),
      severity: 'success',
      visible: readObjectField(object, 'pickingAvailable') !== undefined,
    },
    {
      id: 'catalog_status',
      label: '카탈로그 상태',
      value: formatSignalForPilot(pilotKey, 'catalog_status', object),
      severity: 'info',
      visible: readObjectField(object, 'catalogStatus') !== undefined,
    },
  ];
}

function buildServiceItemDomainSignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'service_item' as const;
  return [
    {
      id: 'service_status',
      label: '서비스 상태',
      value: formatSignalForPilot(pilotKey, 'service_status', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'service_status', object),
    },
    {
      id: 'delivery_window',
      label: '납기 창',
      value: formatSignalForPilot(pilotKey, 'delivery_window', object),
      severity: 'warning',
      visible: isPilotSignalFieldPresent(pilotKey, 'delivery_window', object),
    },
    {
      id: 'linked_contract',
      label: '연결 계약',
      value: formatSignalForPilot(pilotKey, 'linked_contract', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'linked_contract', object),
    },
    {
      id: 'catalog_status',
      label: '카탈로그 상태',
      value: formatSignalForPilot(pilotKey, 'catalog_status', object),
      severity: 'info',
      visible: readObjectField(object, 'catalogStatus') !== undefined,
    },
  ];
}

function buildManufacturingBomSignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'manufacturing_bom' as const;
  return [
    {
      id: 'bom_version',
      label: 'BOM 버전',
      value: formatSignalForPilot(pilotKey, 'bom_version', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'bom_version', object),
    },
    {
      id: 'component_count',
      label: '구성품 수',
      value: formatSignalForPilot(pilotKey, 'component_count', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'component_count', object),
    },
    {
      id: 'revision_status',
      label: '개정 상태',
      value: formatSignalForPilot(pilotKey, 'revision_status', object),
      severity: 'warning',
      visible: isPilotSignalFieldPresent(pilotKey, 'revision_status', object),
    },
    {
      id: 'approval_flags',
      label: '승인 플래그',
      value: formatSignalForPilot(pilotKey, 'approval_flags', object),
      severity: 'warning',
      visible: readObjectField(object, 'approvalFlags') !== undefined,
    },
  ];
}

function buildManufacturingProductionOrderSignals(
  object: Record<string, unknown>
): InspectorSignal[] {
  const pilotKey = 'manufacturing_production_order' as const;
  return [
    {
      id: 'production_status',
      label: '생산 상태',
      value: formatSignalForPilot(pilotKey, 'production_status', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'production_status', object),
    },
    {
      id: 'bom_link',
      label: 'BOM 연결',
      value: formatSignalForPilot(pilotKey, 'bom_link', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'bom_link', object),
    },
    {
      id: 'quality_hold',
      label: '품질 홀드',
      value: formatSignalForPilot(pilotKey, 'quality_hold', object),
      severity: 'danger',
      visible: Boolean(readObjectField(object, 'qualityHold')),
    },
    {
      id: 'schedule_variance',
      label: '일정 편차',
      value: formatSignalForPilot(pilotKey, 'schedule_variance', object),
      severity: 'warning',
      visible: readObjectField(object, 'scheduleVariance') !== undefined,
    },
  ];
}

function buildEntityCustomerProxySignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'entity_customer' as const;
  return [
    {
      id: 'ar_aging',
      label: 'AR 연체',
      value: formatSignalForPilot(pilotKey, 'ar_aging', object),
      severity: 'warning',
      visible: readObjectField(object, 'arAgingDays') !== undefined,
    },
    {
      id: 'credit_hold',
      label: '신용 보류',
      value: formatSignalForPilot(pilotKey, 'credit_hold', object),
      severity: 'danger',
      visible: Boolean(readObjectField(object, 'creditHold')),
    },
    {
      id: 'relation_count',
      label: '연결 관계',
      value: formatSignalForPilot(pilotKey, 'relation_count', object),
      severity: 'info',
      visible: readObjectField(object, 'relationCount') !== undefined,
    },
    {
      id: 'linked_documents',
      label: '연결 문서',
      value: formatSignalForPilot(pilotKey, 'linked_documents', object),
      severity: 'info',
      visible: readObjectField(object, 'linkedDocuments') !== undefined,
    },
  ];
}

function buildEntitySupplierProxySignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'entity_supplier' as const;
  return [
    {
      id: 'relation_count',
      label: '연결 관계',
      value: formatSignalForPilot(pilotKey, 'relation_count', object),
      severity: 'info',
      visible: readObjectField(object, 'relationCount') !== undefined,
    },
    {
      id: 'permission_status',
      label: '권한 상태',
      value: formatSignalForPilot(pilotKey, 'permission_status', object),
      severity: 'warning',
      visible: readObjectField(object, 'permissionStatus') !== undefined,
    },
    {
      id: 'linked_documents',
      label: '연결 문서',
      value: formatSignalForPilot(pilotKey, 'linked_documents', object),
      severity: 'info',
      visible: readObjectField(object, 'linkedDocuments') !== undefined,
    },
    {
      id: 'supplier_rating',
      label: '공급사 등급',
      value: formatSignalForPilot(pilotKey, 'supplier_rating', object),
      severity: 'info',
      visible: readObjectField(object, 'supplierRating') !== undefined,
    },
  ];
}

function buildFinanceCollectionProxySignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'finance_collection' as const;
  return [
    {
      id: 'ar_aging',
      label: 'AR 연체',
      value: formatSignalForPilot(pilotKey, 'ar_aging', object),
      severity: 'warning',
      visible: readObjectField(object, 'arAgingDays') !== undefined,
    },
    {
      id: 'credit_hold',
      label: '신용 보류',
      value: formatSignalForPilot(pilotKey, 'credit_hold', object),
      severity: 'danger',
      visible: Boolean(readObjectField(object, 'creditHold')),
    },
    {
      id: 'overdue_amount',
      label: '연체 금액',
      value: formatSignalForPilot(pilotKey, 'overdue_amount', object),
      severity: 'danger',
      visible: Number(readObjectField(object, 'overdueAmount') ?? 0) > 0,
    },
    {
      id: 'payment_behavior',
      label: '결제 성향',
      value: formatSignalForPilot(pilotKey, 'payment_behavior', object),
      severity: 'warning',
      visible: readObjectField(object, 'paymentBehavior') !== undefined,
    },
  ];
}

function buildKnowledgeDocumentProxySignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'knowledge_document' as const;
  return [
    {
      id: 'document_type',
      label: '문서 유형',
      value: formatSignalForPilot(pilotKey, 'document_type', object),
      severity: 'info',
      visible: readObjectField(object, 'documentType') !== undefined,
    },
    {
      id: 'linked_documents',
      label: '연결 문서',
      value: formatSignalForPilot(pilotKey, 'linked_documents', object),
      severity: 'info',
      visible: readObjectField(object, 'linkedDocuments') !== undefined,
    },
    {
      id: 'last_activity',
      label: '최근 활동',
      value: formatSignalForPilot(pilotKey, 'last_activity', object),
      severity: 'info',
      visible: readObjectField(object, 'lastActivityAt') !== undefined,
    },
    {
      id: 'validation_status',
      label: '검증 상태',
      value: formatSignalForPilot(pilotKey, 'validation_status', object),
      severity: 'warning',
      visible: readObjectField(object, 'validationStatus') !== undefined,
    },
  ];
}

function buildWarehousePickingSignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'warehouse_picking' as const;
  return [
    {
      id: 'picking_status',
      label: '피킹 상태',
      value: formatSignalForPilot(pilotKey, 'picking_status', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'picking_status', object),
    },
    {
      id: 'dispatch_queue',
      label: '출고 대기열',
      value: formatSignalForPilot(pilotKey, 'dispatch_queue', object),
      severity: 'warning',
      visible: readObjectField(object, 'dispatchQueue') !== undefined,
    },
    {
      id: 'stock_link',
      label: '재고 연결',
      value: formatSignalForPilot(pilotKey, 'stock_link', object),
      severity: 'info',
      visible: readObjectField(object, 'stockLink') !== undefined,
    },
    {
      id: 'picking_availability',
      label: '피킹 가능',
      value: formatSignalForPilot(pilotKey, 'picking_availability', object),
      severity: 'success',
      visible: readObjectField(object, 'pickingAvailable') !== undefined,
    },
  ];
}

function buildOrderProxySignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'order' as const;
  return [
    {
      id: 'order_status',
      label: '주문 상태',
      value: formatSignalForPilot(pilotKey, 'order_status', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'order_status', object),
    },
    {
      id: 'fulfillment_stage',
      label: '이행 단계',
      value: formatSignalForPilot(pilotKey, 'fulfillment_stage', object),
      severity: 'warning',
      visible: isPilotSignalFieldPresent(pilotKey, 'fulfillment_stage', object),
    },
    {
      id: 'linked_customer',
      label: '연결 고객',
      value: formatSignalForPilot(pilotKey, 'linked_customer', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'linked_customer', object),
    },
    {
      id: 'schedule_variance',
      label: '일정 편차',
      value: formatSignalForPilot(pilotKey, 'schedule_variance', object),
      severity: 'warning',
      visible: readObjectField(object, 'scheduleVariance') !== undefined,
    },
    {
      id: 'overdue_delivery',
      label: '납기 초과',
      value: formatSignalForPilot(pilotKey, 'overdue_delivery', object),
      severity: 'danger',
      visible:
        Boolean(readObjectField(object, ['overdueDelivery', 'overdue_delivery'])) ||
        Number(readObjectField(object, ['deliveryOverdueDays', 'delivery_overdue_days']) ?? 0) > 0,
    },
    {
      id: 'delivery_evidence_missing',
      label: '납품 증빙 누락',
      value: formatSignalForPilot(pilotKey, 'delivery_evidence_missing', object),
      severity: 'warning',
      visible:
        Boolean(
          readObjectField(object, [
            'deliveryEvidenceMissing',
            'delivery_evidence_missing',
            'missingDeliveryEvidence',
          ])
        ) ||
        Number(
          readObjectField(object, [
            'missingDeliveryEvidenceCount',
            'missing_delivery_evidence_count',
          ]) ?? 0
        ) > 0,
    },
    {
      id: 'production_queue',
      label: '생산 대기열',
      value: formatSignalForPilot(pilotKey, 'production_queue', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'production_queue', object),
    },
  ];
}

function buildInventoryShortageSignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'inventory_shortage' as const;
  return [
    {
      id: 'shortage_quantity',
      label: '부족 수량',
      value: formatSignalForPilot(pilotKey, 'shortage_quantity', object),
      severity: 'danger',
      visible: isPilotSignalFieldPresent(pilotKey, 'shortage_quantity', object),
    },
    {
      id: 'replenishment_status',
      label: '보충 상태',
      value: formatSignalForPilot(pilotKey, 'replenishment_status', object),
      severity: 'warning',
      visible: isPilotSignalFieldPresent(pilotKey, 'replenishment_status', object),
    },
    {
      id: 'stock_link',
      label: '재고 연결',
      value: formatSignalForPilot(pilotKey, 'stock_link', object),
      severity: 'info',
      visible: readObjectField(object, 'stockLink') !== undefined,
    },
    {
      id: 'picking_availability',
      label: '피킹 가능',
      value: formatSignalForPilot(pilotKey, 'picking_availability', object),
      severity: 'success',
      visible: readObjectField(object, 'pickingAvailable') !== undefined,
    },
  ];
}

function buildTaskExecutionSignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'task_execution' as const;
  const missingEvidenceCount = readObjectField(object, [
    'missingEvidenceTasks',
    'missing_evidence_tasks',
    'missingEvidenceTaskCount',
  ]);
  const overdueCount = readObjectField(object, ['overdueAssignments', 'overdue_assignments']);

  return [
    {
      id: 'task_status',
      label: '업무 상태',
      value: formatSignalForPilot(pilotKey, 'task_status', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'task_status', object),
    },
    {
      id: 'missing_evidence_tasks',
      label: '증빙 누락 작업',
      value: formatSignalForPilot(pilotKey, 'missing_evidence_tasks', object),
      severity: 'warning',
      visible:
        Number(missingEvidenceCount ?? 0) > 0 ||
        Boolean(readObjectField(object, ['missingEvidence', 'missing_evidence'])),
    },
    {
      id: 'overdue_assignments',
      label: '기한 초과 배정',
      value: formatSignalForPilot(pilotKey, 'overdue_assignments', object),
      severity: 'danger',
      visible: Number(overdueCount ?? 0) > 0,
    },
    {
      id: 'assignee_link',
      label: '담당자',
      value: formatSignalForPilot(pilotKey, 'assignee_link', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'assignee_link', object),
    },
    {
      id: 'due_date',
      label: '마감일',
      value: formatSignalForPilot(pilotKey, 'due_date', object),
      severity: 'warning',
      visible: isPilotSignalFieldPresent(pilotKey, 'due_date', object),
    },
    {
      id: 'workflow_stage',
      label: '워크플로우 단계',
      value: formatSignalForPilot(pilotKey, 'workflow_stage', object),
      severity: 'warning',
      visible: isPilotSignalFieldPresent(pilotKey, 'workflow_stage', object),
    },
  ];
}

function buildReferenceDomainSignals(object: Record<string, unknown>): InspectorSignal[] {
  const pilotKey = 'reference' as const;
  return [
    {
      id: 'reference_type',
      label: '참조 유형',
      value: formatSignalForPilot(pilotKey, 'reference_type', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'reference_type', object),
    },
    {
      id: 'linked_records',
      label: '연결 레코드',
      value: formatSignalForPilot(pilotKey, 'linked_records', object),
      severity: 'info',
      visible: isPilotSignalFieldPresent(pilotKey, 'linked_records', object),
    },
    {
      id: 'last_activity',
      label: '최근 활동',
      value: formatSignalForPilot(pilotKey, 'last_activity', object),
      severity: 'info',
      visible: readObjectField(object, 'lastActivityAt') !== undefined,
    },
    {
      id: 'validation_status',
      label: '검증 상태',
      value: formatSignalForPilot(pilotKey, 'validation_status', object),
      severity: 'warning',
      visible: readObjectField(object, 'validationStatus') !== undefined,
    },
  ];
}

export function resolveDomainCockpitPilotKey(
  businessDomain: string
): DomainCockpitPilotKey | undefined {
  const normalized = businessDomain.trim();
  const direct = DOMAIN_TO_PILOT_KEY[normalized];
  if (direct) {
    return direct;
  }
  const proxyPilot = PROXY_DOMAIN_TO_PILOT_KEY[normalized];
  if (proxyPilot) {
    return proxyPilot;
  }
  const proxy = resolveWorkbenchGrammarProxy(normalized);
  if (!proxy) {
    return undefined;
  }
  return DOMAIN_TO_PILOT_KEY[proxy.canonicalBusinessDomain];
}

export function resolveDomainCockpitInvariant(
  businessDomain: string
): DomainCockpitInvariant | undefined {
  const pilotKey = resolveDomainCockpitPilotKey(businessDomain);
  if (!pilotKey) {
    return undefined;
  }
  return DOMAIN_COCKPIT_INVARIANT_MATRIX[pilotKey];
}

export function isDomainCockpitPilotDomain(businessDomain: string): boolean {
  return resolveDomainCockpitPilotKey(businessDomain) !== undefined;
}

export function resolveDomainInspectorSignals(input: {
  businessDomain: string;
  object: Record<string, unknown>;
}): InspectorSignal[] {
  const invariant = resolveDomainCockpitInvariant(input.businessDomain);
  if (!invariant) {
    return [];
  }

  const pilotKey = invariant.pilotKey;
  let rawSignals: InspectorSignal[] = [];

  switch (pilotKey) {
    case 'finance':
      rawSignals = buildFinanceDomainSignals(input.object);
      break;
    case 'entity':
      rawSignals = buildEntityDomainSignals(input.object);
      break;
    case 'entity_customer':
      rawSignals = buildEntityCustomerProxySignals(input.object);
      break;
    case 'entity_supplier':
      rawSignals = buildEntitySupplierProxySignals(input.object);
      break;
    case 'finance_collection':
      rawSignals = buildFinanceCollectionProxySignals(input.object);
      break;
    case 'knowledge_document':
      rawSignals = buildKnowledgeDocumentProxySignals(input.object);
      break;
    case 'item_sellable':
      rawSignals = buildItemSellableDomainSignals(input.object);
      break;
    case 'service_item':
      rawSignals = buildServiceItemDomainSignals(input.object);
      break;
    case 'manufacturing_bom':
      rawSignals = buildManufacturingBomSignals(input.object);
      break;
    case 'manufacturing_production_order':
      rawSignals = buildManufacturingProductionOrderSignals(input.object);
      break;
    case 'warehouse_picking':
      rawSignals = buildWarehousePickingSignals(input.object);
      break;
    case 'order':
      rawSignals = buildOrderProxySignals(input.object);
      break;
    case 'inventory_shortage':
      rawSignals = buildInventoryShortageSignals(input.object);
      break;
    case 'task_execution':
      rawSignals = buildTaskExecutionSignals(input.object);
      break;
    case 'reference':
      rawSignals = buildReferenceDomainSignals(input.object);
      break;
    default:
      rawSignals = [];
  }

  return sortSignalsByDomainPriority(invariant.inspectorSignalPriority, rawSignals).filter(
    (signal) => signal.visible
  );
}

export function resolveDomainCockpitDataAttributes(
  businessDomain: string
): Record<string, string> | undefined {
  const invariant = resolveDomainCockpitInvariant(businessDomain);
  if (!invariant) {
    return undefined;
  }

  return {
    'data-domain-cockpit': invariant.pilotKey,
    'data-inspector-signal-priority': invariant.inspectorSignalPriority.join(','),
    'data-auxiliary-tab-priority': invariant.auxiliaryTabPriority.join(','),
    'data-action-surface-defaults': invariant.actionSurfaceDefaults.join(','),
    'data-inspector-default-tab': invariant.inspectorDefaultTab,
    'data-ai-layers-in-inspector': String(invariant.aiLayersInInspector),
  };
}
